<template>
    <div>
        <!--begin::Label-->
        <label class="form-label fs-6 fw-bolder  text-dark"
               :class="item.is_required ? 'required' : ''"
        >
            <slot v-if="i18n.locale.value == 'ar'">
                {{item.label_ar}}
            </slot>
            <slot v-if="i18n.locale.value == 'en'">
                {{item.label}}
            </slot>
            <slot v-if="!item.is_required">&nbsp;{{$t('optional')}}</slot>
        </label>
        <!--end::Label-->

        <template v-if="item.type == 'Text' && item.max_lines == 1">
            <!--begin::Input-->
            <Field class="form-control form-control-lg form-control-solid"
                   type="text"
                   :label="i18n.locale.value == 'ar' ? item.label_ar : item.label"
                   :rules="$helpers.getValidationRules(item)"
                   v-model="item.value"
                   :maxlength="item.max_length"
                   :name="item.model_key + item.key"
                   :key="item.key"
                   :disabled="item.hasOwnProperty('is_editable') && !item.is_editable"
                   autocomplete="off"
            />
            <!--end::Input-->
        </template>

        <template v-if="item.type == 'Text' && item.max_lines != 1">
            <!--begin::Input-->
            <Field v-model="item.value"
                   :name='item.model_key + item.key'
                   :key="item.key"
                   :label="i18n.locale.value == 'ar' ? item.label_ar : item.label"
                   :rules="$helpers.getValidationRules(item)"
                   v-slot='{ field }'>
                <textarea v-bind='field'
                          :name="item.model_key + item.key"
                          :maxlength="item.max_length"
                          class='form-control form-control-lg form-control-solid w-100'
                          v-model="item.value"
                          :disabled="item.hasOwnProperty('is_editable') && !item.is_editable"
                          :rows="item.max_lines"/>
            </Field>
            <!--end::Input-->
        </template>

        <template v-if="item.type == 'Number'">
            <!--begin::Input-->
            <Field class="form-control form-control-lg text-start form-control-solid"
                   type="number"
                   :rules="$helpers.getValidationRules(item)"
                   v-model="item.value"
                   :key="item.key"
                   :label="i18n.locale.value == 'ar' ? item.label_ar : item.label"
                   :name="item.model_key + item.key"
                   oninput="javascript: if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);"
                   :maxlength="item.max_length ? item.max_length : 100"
                   :disabled="item.hasOwnProperty('is_editable') && !item.is_editable"
                   autocomplete="off"
            />
            <!--end::Input-->
        </template>

        <template v-if="item.type == 'Phone'">
            <!--begin::Input-->
            <Field class="form-control form-control-lg text-start form-control-solid"
                   type="tel"
                   :rules="$helpers.getValidationRules(item)"
                   v-model="item.value"
                   :key="item.key"
                   :label="i18n.locale.value == 'ar' ? item.label_ar : item.label"
                   :name="item.model_key + item.key"
                   :disabled="item.hasOwnProperty('is_editable') && !item.is_editable"
                   autocomplete="off"
            />
            <!--end::Input-->
        </template>

        <template v-if="item.type == 'Email'">
            <!--begin::Input-->
            <Field class="form-control form-control-lg form-control-solid"
                   type="email"
                   :rules="$helpers.getValidationRules(item)"
                   v-model="item.value"
                   :key="item.key"
                   :label="i18n.locale.value == 'ar' ? item.label_ar : item.label"
                   :name="item.model_key + item.key"
                   :disabled="item.hasOwnProperty('is_editable') && !item.is_editable"
                   autocomplete="off"
            />
            <!--end::Input-->
        </template>

        <Field
                v-model="item.value"
                :key="item.key"
                :label="i18n.locale.value == 'ar' ? item.label_ar : item.label"
                :name='item.model_key + item.key'
                type="hidden"
                class="d-none"/>

        <div class="fv-plugins-message-container">
            <div class="fv-help-block">
                <!--<span class="text-dark">({{item.type}})</span>-->
                <slot v-if="i18n.locale.value == 'ar'">
                    <span class="text-muted">{{item.hint_text_ar}}</span>
                </slot>
                <slot v-if="i18n.locale.value == 'en'">
                    <span class="text-muted">{{item.hint_text_en}}</span>
                </slot>
            </div>
        </div>
        <div class="fv-plugins-message-container">
            <div class="fv-help-block">
                <!--<ErrorMessage :name="item.model_key + item.key"/>-->
                <ErrorMessage :name="item.model_key + item.key" v-slot="{ message }">
                    <template v-if="item.validation_error_msg_ar  && i18n.locale.value == 'ar'">
                        <slot>
                            {{item.validation_error_msg_ar}}
                        </slot>
                    </template>
                    <template v-if="item.validation_error_msg  && i18n.locale.value == 'en'">
                        <slot>
                            {{item.validation_error_msg}}
                        </slot>
                    </template>
                    <template
                            v-if="!(item.validation_error_msg_ar  && i18n.locale.value == 'ar')  && !(item.validation_error_msg  && i18n.locale.value == 'en')">
                        <slot>
                            {{message}}
                        </slot>
                    </template>
                </ErrorMessage>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
  import { defineComponent, ref, reactive, toRefs, inject, computed, onMounted } from 'vue';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { useI18n } from 'vue-i18n';
  import { ErrorMessage, Field, Form } from 'vee-validate';


  export default defineComponent({
    name: 'FormInput',
    components: {
      Field,
      ErrorMessage,
    },
    props: {
      item: Object,
    },
    setup(props, context) {
      const helpers: any = inject('helpers');
      const i18n = useI18n();

      let currentItem: any = props.item;
      // currentItem.key = Math.random().toString(16);
      const state = reactive({
        item: currentItem,
      });


      onMounted(() => {

        let item: any = state.item;
        if (!item.hasOwnProperty('value')) {
          item.value = null;
        }
        if (!item.hasOwnProperty('max_lines')) {
          item.max_lines = 1;
        }
        if (!item.hasOwnProperty('min_lines')) {
          item.min_lines = 1;
        }
        if (!item.hasOwnProperty('is_required')) {
          item.is_required = true;
        }
        if (!item.hasOwnProperty('is_editable')) {
          item.is_required = true;
        }

        if (item.type == 'Number') {
          let value = JSON.parse(JSON.stringify(item.value));
          if (value){
            item.value = ConvertToArabicNumbers(value);
          }
        }
      });

      const ConvertToArabicNumbers = (string) => {
        return string.replace(/[\u0660-\u0669]/g, function (c) {
          return c.charCodeAt(0) - 0x0660;
        }).replace(/[\u06f0-\u06f9]/g, function (c) {
          return c.charCodeAt(0) - 0x06f0;
        });
      };

      return {
        ...toRefs(state),
        i18n,
        helpers,
      };
    },
  });
</script>
